import React, { useState, useMemo } from 'react';
import { CAMERA_BRANDS, CameraBrand, CameraModel } from '../../constants/cameraFtpData';
import styles from './CameraInstructions.module.css';

interface CameraInstructionsProps {
  host: string;
  port: number;
}

/* ─── Brand Logo / Letter Badge ──────────────────────────────────────────── */

function BrandBadge({ brand, size = 'md' }: { brand: CameraBrand; size?: 'sm' | 'md' }) {
  return (
    <span
      className={[styles['brand-badge'], size === 'sm' ? styles['brand-badge-sm'] : styles['brand-badge-md'], styles['brand-badge-dynamic']].join(' ')}
      style={{ '--brand-bg': brand.color + '18', '--brand-color': brand.color } as React.CSSProperties}
    >
      {brand.name[0]}
    </span>
  );
}

/* ─── Step Row ───────────────────────────────────────────────────────────── */

function Step({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <li className={styles['step-row']}>
      <span className={styles['step-number']}>
        {number}
      </span>
      <span className={styles['step-text']}>{children}</span>
    </li>
  );
}

/* ─── Menu Path Display ──────────────────────────────────────────────────── */

function MenuPath({ path }: { path: string }) {
  return <code className={styles['menu-path']}>{path}</code>;
}

/* ─── No FTP Support Card ────────────────────────────────────────────────── */

function NoFtpCard({ camera, brand }: { camera: CameraModel; brand: CameraBrand }) {
  return (
    <div className={styles['container']}>
      <div className={styles['no-ftp-alert']}>
        <div className={styles['no-ftp-alert-inner']}>
          <svg className={styles['alert-icon-lg']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <h4 className={styles['no-ftp-title']}>No FTP Support</h4>
            <p className={styles['no-ftp-text']}>
              The <span className={styles['no-ftp-strong']}>{brand.name} {camera.model}</span> does not have built-in FTP transfer.
              You will need to <span className={styles['font-medium']}>manually upload photos</span> to Pixel Machha.
            </p>
          </div>
        </div>
      </div>

      {camera.alternativeMethod && (
        <div className={styles['alt-method-card']}>
          <h5 className={styles['alt-method-title']}>Alternative Workflow</h5>
          <p className={styles['alt-method-text']}>{camera.alternativeMethod}</p>
          {camera.companionApp && (
            <p className={styles['companion-app']}>
              Companion app: <span className={styles['companion-app-name']}>{camera.companionApp}</span>
            </p>
          )}
        </div>
      )}

      {camera.ftpAccessoryNote && (
        <div className={styles['amber-note']}>
          <p className={styles['amber-note-text']}>{camera.ftpAccessoryNote}</p>
        </div>
      )}

      <a
        href={camera.manualUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles['ext-link']}
      >
        <svg className={styles['ext-link-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        Official User Manual
      </a>
    </div>
  );
}

/* ─── FTP Instructions Card ──────────────────────────────────────────────── */

function FtpInstructionsCard({ camera, brand, host, port }: { camera: CameraModel; brand: CameraBrand; host: string; port: number }) {
  return (
    <div className={styles['container']}>
      {/* Critical settings banner */}
      <div className={styles['critical-banner']}>
        <div className={styles['critical-inner']}>
          <svg className={styles['alert-icon-sm']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <h4 className={styles['critical-title']}>Critical for Fast Uploads</h4>
            <p className={styles['critical-text']}>
              You <span className={styles['critical-bold']}>must</span> configure all three settings below.
              Uploading RAW files or full-size JPEGs will cause extremely slow transfers and may clog the upload queue during events.
              Small HEIF/JPEG files are perfectly sufficient for gallery display, social media sharing, and overlay application.
            </p>
          </div>
        </div>
      </div>

      {/* Step 1: Image Quality */}
      <section className={styles['instruction-section']}>
        <div className={styles['instruction-header']}>
          <span className={[styles['instruction-number'], styles['instruction-number-orange']].join(' ')}>1</span>
          <h5 className={styles['instruction-title']}>Set Image Quality to Smallest HEIF/JPEG</h5>
        </div>
        <div className={styles['instruction-body']}>
          {camera.heifSupported && camera.heifEnablePath && (
            <div>
              <p className={styles['instruction-text']}>
                <span className={styles['instruction-strong']}>Enable HEIF</span> (recommended — ~50% smaller than JPEG):
              </p>
              <MenuPath path={camera.heifEnablePath} />
            </div>
          )}
          <div>
            <p className={styles['instruction-text']}>
              <span className={styles['instruction-strong']}>Set quality to smallest</span>:
            </p>
            {camera.imageQualityPath && <MenuPath path={camera.imageQualityPath} />}
            {camera.imageSizePath && (
              <div className={styles['mt-xs']}>
                <MenuPath path={camera.imageSizePath} />
              </div>
            )}
            <p className={styles['instruction-hint']}>
              Select: <span className={styles['instruction-highlight']}>{camera.smallestQuality}</span>
              {camera.smallestSize && <> &mdash; size <span className={styles['instruction-highlight']}>{camera.smallestSize}</span></>}
            </p>
          </div>
          <div className={styles['teal-tip']}>
            <p className={styles['teal-tip-text']}>
              This quality is absolutely fine for the purpose it serves. Photos are for instant gallery sharing, social media, and overlays — not print.
              {camera.heifSupported && ' HEIF gives even smaller files at the same visual quality.'}
            </p>
          </div>
        </div>
      </section>

      {/* Recommended: Shoot RAW+JPEG */}
      <section className={styles['instruction-section']}>
        <div className={styles['instruction-header']}>
          <span className={[styles['instruction-number'], styles['instruction-number-orange']].join(' ')}>2</span>
          <h5 className={styles['instruction-title']}>
            Shoot RAW + {camera.heifSupported ? 'HEIF/JPEG' : 'JPEG'} (Recommended)
          </h5>
        </div>
        <div className={styles['instruction-body']}>
          <p className={styles['instruction-text']}>
            Set your camera to shoot <span className={styles['instruction-strong']}>RAW + {camera.heifSupported ? 'HEIF' : 'JPEG'} ({camera.smallestSize || 'smallest'})</span>.
            This keeps full-resolution RAW files on your memory card for post-processing, while only the tiny {camera.heifSupported ? 'HEIF/JPEG' : 'JPEG'} gets uploaded via FTP.
          </p>
          {camera.imageQualityPath && <MenuPath path={camera.imageQualityPath} />}
          <p className={styles['instruction-hint']}>
            Select: <span className={styles['instruction-highlight-sky']}>RAW + {camera.heifSupported ? 'HEIF' : 'JPEG'} {camera.smallestSize || ''}</span>
          </p>
        </div>
      </section>

      {/* Step 3: Auto Transfer */}
      <section className={styles['instruction-section']}>
        <div className={styles['instruction-header']}>
          <span className={[styles['instruction-number'], styles['instruction-number-orange']].join(' ')}>3</span>
          <h5 className={styles['instruction-title']}>Enable Auto Upload</h5>
        </div>
        <div className={styles['instruction-body']}>
          <p className={styles['instruction-text']}>
            Enable <span className={styles['instruction-strong']}>{brand.autoTransferTerm}</span> so every photo is automatically queued for FTP upload the moment you press the shutter.
            No manual action needed during the event.
          </p>
          {camera.autoTransferPath && <MenuPath path={camera.autoTransferPath} />}
        </div>
      </section>

      {/* Step 4: Transfer Target */}
      <section className={styles['instruction-section']}>
        <div className={styles['instruction-header']}>
          <span className={[styles['instruction-number'], styles['instruction-number-orange']].join(' ')}>4</span>
          <h5 className={styles['instruction-title']}>
            Upload {camera.heifSupported ? 'HEIF/JPEG' : 'JPEG'} Only (Not RAW)
          </h5>
        </div>
        <div className={styles['instruction-body']}>
          <p className={styles['instruction-text']}>
            Set <span className={styles['instruction-strong']}>{brand.transferTargetTerm}</span> to upload <span className={styles['instruction-highlight']}>{camera.heifSupported ? 'HEIF Only' : 'JPEG Only'}</span>.
            This ensures only the small preview file is sent over FTP — RAW files stay safely on the memory card.
          </p>
          {camera.transferTargetPath && <MenuPath path={camera.transferTargetPath} />}
          {camera.transferTargetOptions && (
            <p className={styles['transfer-options']}>
              Available options: {camera.transferTargetOptions.map((opt, i) => (
                <span key={opt}>
                  {i > 0 && ' / '}
                  <span className={opt.toLowerCase().includes('jpeg only') || opt.toLowerCase().includes('heif only') ? styles['instruction-highlight'] : ''}>
                    {opt}
                  </span>
                </span>
              ))}
            </p>
          )}
        </div>
      </section>

      {/* Step 5: FTP Server Connection */}
      <section className={styles['instruction-section']}>
        <div className={styles['instruction-header']}>
          <span className={[styles['instruction-number'], styles['instruction-number-indigo']].join(' ')}>5</span>
          <h5 className={styles['instruction-title']}>Configure FTP Server</h5>
        </div>
        <div className={styles['instruction-body']}>
          {camera.ftpSetupPath && <MenuPath path={camera.ftpSetupPath} />}
          <ol className={styles['steps-list']}>
            <Step number={1}>Connection type: <span className={styles['font-medium']}>Wi-Fi</span> (internet access required)</Step>
            <Step number={2}>Server address: <span className={styles['instruction-highlight-sky']}>{host}</span></Step>
            <Step number={3}>Port: <span className={styles['instruction-highlight-sky']}>{port}</span></Step>
            <Step number={4}><span className={styles['font-medium']}>Passive Mode &rarr; OFF</span> (try ON if connection fails)</Step>
            <Step number={5}><span className={styles['font-medium']}>Secure Protocol &rarr; OFF</span> (this server uses plain FTP)</Step>
            <Step number={6}>Enter your FTP <span className={styles['font-medium']}>username</span> and <span className={styles['font-medium']}>password</span> (from the credentials above)</Step>
            <Step number={7}>Target folder / directory: <span className={styles['instruction-highlight-sky']}>/</span></Step>
          </ol>
        </div>
      </section>

      {/* Firmware note */}
      {camera.firmwareNote && (
        <div className={styles['firmware-note']}>
          <p className={styles['firmware-text']}>
            <span className={styles['amber-note-bold']}>Note:</span> {camera.firmwareNote}
          </p>
        </div>
      )}

      {/* Connectivity info */}
      <div className={styles['connectivity-row']}>
        <span className={styles['connectivity-label']}>Connectivity:</span>
        {camera.connectivity.map(c => (
          <span key={c} className={styles['connectivity-tag']}>{c}</span>
        ))}
        {camera.heifSupported && (
          <span className={styles['heif-tag']}>HEIF</span>
        )}
      </div>

      {/* Manual link */}
      <div className={styles['manual-row']}>
        <a
          href={camera.manualUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles['ext-link']}
        >
          <svg className={styles['ext-link-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Official User Manual
        </a>
        {camera.manualFtpPages && (
          <span className={styles['manual-pages']}>FTP section: {camera.manualFtpPages}</span>
        )}
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

export const CameraInstructions: React.FC<CameraInstructionsProps> = ({ host, port }) => {
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [selectedModelName, setSelectedModelName] = useState<string | null>(null);

  const selectedBrand = useMemo(
    () => CAMERA_BRANDS.find(b => b.id === selectedBrandId) ?? null,
    [selectedBrandId],
  );

  const selectedCamera = useMemo(
    () => selectedBrand?.models.find(m => m.model === selectedModelName) ?? null,
    [selectedBrand, selectedModelName],
  );

  const ftpModels = useMemo(
    () => selectedBrand?.models.filter(m => m.ftpSupported) ?? [],
    [selectedBrand],
  );

  const nonFtpModels = useMemo(
    () => selectedBrand?.models.filter(m => !m.ftpSupported) ?? [],
    [selectedBrand],
  );

  return (
    <div className={styles['container']}>
      {/* Brand Selector */}
      <div>
        <h4 className={styles['section-heading']}>Select your camera brand</h4>
        <div className={styles['brand-grid']}>
          {CAMERA_BRANDS.map(brand => (
            <button
              key={brand.id}
              onClick={() => {
                setSelectedBrandId(brand.id === selectedBrandId ? null : brand.id);
                setSelectedModelName(null);
              }}
              className={[
                styles['brand-btn'],
                brand.id === selectedBrandId ? styles['brand-btn-active'] : '',
              ].filter(Boolean).join(' ')}
            >
              <BrandBadge brand={brand} />
              <span className={styles['brand-name']}>{brand.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Model Selector */}
      {selectedBrand && (
        <div>
          <h4 className={styles['section-heading']}>
            Select your {selectedBrand.name} model
          </h4>

          {/* FTP-capable models */}
          {ftpModels.length > 0 && (
            <div className={styles['model-section']}>
              <p className={styles['model-indicator']}>
                <span className={[styles['indicator-dot'], styles['indicator-dot-ftp']].join(' ')} />
                FTP Supported
              </p>
              <div className={styles['model-list']}>
                {ftpModels.map(cam => (
                  <button
                    key={cam.model}
                    onClick={() => setSelectedModelName(cam.model === selectedModelName ? null : cam.model)}
                    className={[
                      styles['model-btn'],
                      cam.model === selectedModelName ? styles['model-btn-active-ftp'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {cam.model}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Non-FTP models */}
          {nonFtpModels.length > 0 && (
            <div>
              <p className={styles['model-indicator']}>
                <span className={[styles['indicator-dot'], styles['indicator-dot-noftp']].join(' ')} />
                No FTP — Manual Upload Required
              </p>
              <div className={styles['model-list']}>
                {nonFtpModels.map(cam => (
                  <button
                    key={cam.model}
                    onClick={() => setSelectedModelName(cam.model === selectedModelName ? null : cam.model)}
                    className={[
                      styles['model-btn'],
                      styles['model-btn-noftp'],
                      cam.model === selectedModelName ? styles['model-btn-active-noftp'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {cam.model}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions for selected camera */}
      {selectedBrand && selectedCamera && (
        <div className={styles['camera-header']}>
          <div className={styles['camera-header-row']}>
            <BrandBadge brand={selectedBrand} size="sm" />
            <h4 className={styles['camera-title']}>
              {selectedBrand.name} {selectedCamera.model}
            </h4>
            {selectedCamera.ftpSupported ? (
              <span className={[styles['ftp-badge'], styles['ftp-badge-ready']].join(' ')}>FTP Ready</span>
            ) : (
              <span className={[styles['ftp-badge'], styles['ftp-badge-noftp']].join(' ')}>No FTP</span>
            )}
          </div>

          {selectedCamera.ftpSupported ? (
            <FtpInstructionsCard camera={selectedCamera} brand={selectedBrand} host={host} port={port} />
          ) : (
            <NoFtpCard camera={selectedCamera} brand={selectedBrand} />
          )}
        </div>
      )}

      {/* Show generic quick-start when no camera is selected */}
      {!selectedBrand && (
        <div className={styles['quick-start-card']}>
          <h4 className={styles['quick-start-title']}>Quick Start (Any Camera)</h4>
          <p className={styles['quick-start-text']}>
            Select your camera brand and model above for step-by-step instructions.
            Here is the general setup for any FTP-capable camera:
          </p>
          <ol className={styles['steps-list']}>
            <Step number={1}><span className={styles['quick-start-orange']}>Set image quality to smallest JPEG/HEIF</span> — this is critical for fast uploads. The small file size is perfect for gallery display and social sharing.</Step>
            <Step number={2}><span className={styles['quick-start-orange']}>Shoot RAW + small JPEG/HEIF</span> — keeps full-res RAW on card, uploads only the tiny JPEG/HEIF.</Step>
            <Step number={3}><span className={styles['quick-start-orange']}>Enable auto-upload</span> — every photo uploads automatically when you press the shutter.</Step>
            <Step number={4}><span className={styles['quick-start-orange']}>Set upload target to JPEG/HEIF only</span> — never upload RAW files via FTP.</Step>
            <Step number={5}>Connect to FTP server: <span className={styles['quick-start-sky']}>{host}:{port}</span>, Passive Mode OFF, Secure Protocol OFF</Step>
            <Step number={6}>Enter your FTP username and password, target directory: <span className={styles['quick-start-sky']}>/</span></Step>
          </ol>
        </div>
      )}

      {/* Desktop client fallback */}
      <section className={styles['desktop-client-card']}>
        <h4 className={styles['desktop-client-title']}>
          <span className={styles['desktop-icon-wrapper']}>
            <svg className={styles['desktop-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </span>
          Desktop FTP Client (FileZilla, Cyberduck)
        </h4>
        <p className={styles['desktop-hint']}>For cameras without FTP, or for manual batch uploads from a computer:</p>
        <ol className={styles['steps-list']}>
          <Step number={1}>Host: <span className={styles['instruction-highlight-sky']}>{host}</span>, Port: <span className={styles['instruction-highlight-sky']}>{port}</span>, Protocol: <span className={styles['instruction-highlight-sky']}>FTP (plain, no TLS)</span></Step>
          <Step number={2}>Enter your FTP <span className={styles['font-medium']}>username</span> and <span className={styles['font-medium']}>password</span></Step>
          <Step number={3}>Transfer mode: <span className={styles['font-medium']}>Active</span> (try Passive if Active fails)</Step>
          <Step number={4}>Upload photos to root <span className={styles['instruction-highlight-sky']}>/</span></Step>
        </ol>
      </section>
    </div>
  );
};
